import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { colors, fonts } from '../brand';
import { useAuth } from '../context/AuthContext';
import { API } from '../context/AuthContext';
import Disclaimer from '../components/Disclaimer';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

// ---------------------------------------------------------------------------
// Story metadata — display only, no audio URLs here.
// The backend (api/audio/<id>) generates short-lived signed URLs on demand.
// Add/edit stories here and in backend/app/routes/audio.py together.
// ---------------------------------------------------------------------------
const stories = [
  {
    id: 2,
    section: 'free',
    title: 'Enough Is Enough: Why I Started Elephants Answering',
    speaker: 'Amy, Founder',
    duration: '4:42',
    description: 'Shedding light on the courts through my own experience, and answering with stories, resources, and a refuge for women going through divorce.',
  },
  {
    id: 3,
    section: 'member',
    title: 'Blindsided. Iced Out. Still Standing.',
    speaker: 'Stephanie, community member',
    duration: '9:31',
    description: 'When the ground gives way, you build a new one — even across oceans. With patience and hard work.',
  },
  {
    id: 8,
    section: 'member',
    title: "I've Never Been Happier",
    speaker: 'Anna, Community Member',
    duration: '2:17',
    description: 'Life after my $450 DIY divorce — the scary first step that worked out.',
  },
  {
    id: 4,
    section: 'member',
    title: 'Topic — TBD',
    speaker: 'Community member',
    duration: '',
    description: 'Coming soon.',
  },
  {
    id: 7,
    section: 'founder',
    title: 'The Court of Appeals, Demystified — Buckle Up',
    speaker: 'Amy, Founder',
    duration: '4:53',
    description: 'Everyone talks about the judges\' orders. No one mentions what happens when someone disagrees — the notice is filed, the clock starts, the bills follow. Here\'s my timeline, and three questions worth asking your attorney upfront.',
  },
];

function StoryCard({ story, isSubscribed, token, user }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false); // true once signed URL is loaded

  const canPlay = story.section === 'free' || isSubscribed;

  const handlePlay = async () => {
    setError(null);

    // If audio is already loaded, just toggle play/pause
    if (ready && audioRef.current) {
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
      } else {
        audioRef.current.play();
        setPlaying(true);
      }
      return;
    }

    // Fetch a signed URL from the backend
    setLoading(true);
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API}/api/audio/${story.id}`, { headers });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Could not load audio.');
        setLoading(false);
        return;
      }

      audioRef.current.src = data.url;
      audioRef.current.load();
      audioRef.current.play();
      setReady(true);
      setPlaying(true);
    } catch (e) {
      setError('Something went wrong loading this clip.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardTop}>
        <div style={styles.cardMeta}>
          {story.section === 'free'
            ? <span style={styles.freeBadge}>Free</span>
            : story.section === 'founder'
            ? <span style={styles.founderBadge}>Founder</span>
            : <span style={styles.memberBadge}>Members</span>
          }
          <span style={styles.duration}>{story.duration}</span>
        </div>
        <h3 style={styles.cardTitle}>{story.title}</h3>
        <p style={styles.cardSpeaker}>{story.speaker}</p>
        <p style={styles.cardDesc}>{story.description}</p>
      </div>

      <div style={styles.cardBottom}>
        {story.comingSoon ? (
          <p style={styles.comingSoonText}>Coming soon</p>
        ) : canPlay ? (
          <>
            <button style={styles.playBtn} onClick={handlePlay} disabled={loading}>
              {loading ? 'Loading...' : playing ? '⏸ Pause' : '▶ Play'}
            </button>
            {error && <p style={styles.errorText}>{error}</p>}
            <audio
              ref={audioRef}
              onEnded={() => setPlaying(false)}
              onError={() => { setError('Audio unavailable — file may not be uploaded yet.'); setPlaying(false); }}
            />
          </>
        ) : null}
      </div>
      {/* StoryNoteForm hidden — re-enable when ready */}
    </div>
  );
}

function StoryNoteForm({ story, user, token, canPlay }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // Stories marked "coming soon" can't receive notes
  if (!story.duration) return null;

  if (!user) {
    return (
      <p style={noteStyles.loginPrompt}>
        <Link to="/login" style={noteStyles.loginLink}>Log in</Link> to send a note to this storyteller.
      </p>
    );
  }

  // Free story → any logged-in user. Members story → active subscriber only.
  if (!canPlay) {
    return null; // locked card already shows the join prompt
  }

  if (sent) {
    return <p style={noteStyles.thanks}>Your note is on its way. — Amy</p>;
  }

  if (!open) {
    return (
      <button style={noteStyles.toggleBtn} onClick={() => setOpen(true)}>
        Send a note to this storyteller
      </button>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/messages/story-note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ story_id: story.id, message }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
      } else {
        setSent(true);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={noteStyles.formWrap}>
      <p style={noteStyles.label}>Your note is anonymous — the storyteller won't see your name or email.</p>
      <form onSubmit={handleSubmit} style={noteStyles.form}>
        <textarea
          style={noteStyles.textarea}
          placeholder="Write your note here…"
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={4}
          required
        />
        {error && <p style={noteStyles.error}>{error}</p>}
        <div style={noteStyles.btnRow}>
          <button style={noteStyles.cancelBtn} type="button" onClick={() => setOpen(false)}>Cancel</button>
          <button style={noteStyles.submitBtn} type="submit" disabled={loading || !message.trim()}>
            {loading ? 'Sending…' : 'Send Note'}
          </button>
        </div>
      </form>
    </div>
  );
}

const noteStyles = {
  toggleBtn: { marginTop: '14px', backgroundColor: 'transparent', border: `1px solid #c0736a`, color: '#c0736a', padding: '8px 20px', fontSize: '12px', fontFamily: 'Georgia, serif', cursor: 'pointer', letterSpacing: '0.5px' },
  formWrap: { marginTop: '16px', borderTop: '1px solid #f0e0d8', paddingTop: '16px' },
  label: { fontFamily: 'Georgia, serif', fontSize: '12px', color: '#8b7a7a', fontStyle: 'italic', marginBottom: '10px' },
  form: { display: 'flex', flexDirection: 'column', gap: '10px' },
  textarea: { padding: '12px', border: '1px solid #d4a096', backgroundColor: '#fff', fontSize: '14px', fontFamily: 'Georgia, serif', color: '#1a2e4a', resize: 'vertical', outline: 'none' },
  btnRow: { display: 'flex', gap: '10px' },
  cancelBtn: { padding: '8px 18px', backgroundColor: 'transparent', border: '1px solid #d4a096', color: '#8b7a7a', fontSize: '13px', fontFamily: 'Georgia, serif', cursor: 'pointer' },
  submitBtn: { padding: '8px 24px', backgroundColor: '#c0736a', border: 'none', color: '#fff', fontSize: '13px', fontFamily: 'Georgia, serif', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px' },
  error: { color: '#c0392b', fontSize: '13px', fontFamily: 'Georgia, serif' },
  thanks: { marginTop: '14px', fontFamily: 'Georgia, serif', fontSize: '13px', color: '#1a2e4a', fontStyle: 'italic' },
  loginPrompt: { marginTop: '4px', fontFamily: 'Georgia, serif', fontSize: '13px', color: '#8b7a7a', fontStyle: 'italic' },
  loginLink: { background: 'none', border: 'none', color: '#c0736a', cursor: 'pointer', fontFamily: 'Georgia, serif', fontSize: '13px', fontStyle: 'italic', padding: 0, textDecoration: 'underline' },
};

function VoiceMemoForm() {
  const [title, setTitle] = useState('');
  const [tagline, setTagline] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [file, setFile] = useState(null);
  const [durationError, setDurationError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // Recorder state
  const [recording, setRecording] = useState(false);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [micError, setMicError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const startRecording = async () => {
    setMicError('');
    setDurationError('');
    setRecordedUrl(null);
    setFile(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a MIME type the browser supports (iOS uses mp4, others webm)
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
        : '';
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        const ext = mr.mimeType.includes('mp4') ? 'mp4' : 'webm';
        const f = new File([blob], `voice-memo.${ext}`, { type: mr.mimeType });
        setFile(f);
        setRecordedUrl(URL.createObjectURL(blob));
      };

      mr.start();
      setRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(t => {
          if (t >= 599) { stopRecording(); return 600; }
          return t + 1;
        });
      }, 1000);
    } catch {
      setMicError('Microphone access was denied. Please allow microphone permission and try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const reRecord = () => {
    setRecordedUrl(null);
    setFile(null);
    setRecordingTime(0);
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setDurationError('');
    setRecordedUrl(null);
    const url = URL.createObjectURL(f);
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(url);
      if (audio.duration > 10 * 60) {
        setDurationError('Recording must be 10 minutes or less.');
        setFile(null);
        e.target.value = '';
      } else {
        setFile(f);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('title', title);
      form.append('tagline', tagline);
      form.append('name', name);
      form.append('email', email);
      form.append('audio', file);
      const res = await fetch(`${API}/api/audio/submit-memo`, { method: 'POST', body: form });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Something went wrong.');
      } else {
        setSent(true);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  if (sent) return (
    <div style={memoStyles.thanks}>
      <p style={memoStyles.thanksText}>Thank you for sharing your story. We'll be in touch. — Amy</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} style={memoStyles.form}>
      <input style={memoStyles.input} placeholder="Title / Topic (optional)" value={title} onChange={e => setTitle(e.target.value)} />
      <input style={memoStyles.input} placeholder="Tagline — one sentence about your story (optional)" value={tagline} onChange={e => setTagline(e.target.value)} />
      <input style={memoStyles.input} placeholder="Your name (optional)" value={name} onChange={e => setName(e.target.value)} />
      <input style={memoStyles.input} type="email" placeholder="Your email (required)" value={email} onChange={e => setEmail(e.target.value)} required />

      {/* Recorder */}
      <div style={memoStyles.recorderBox}>
        {!recording && !recordedUrl && (
          <button type="button" style={memoStyles.recordBtn} onClick={startRecording}>
            ● Record Your Story
          </button>
        )}
        {recording && (
          <div style={memoStyles.recordingRow}>
            <span style={memoStyles.recordingDot} />
            <span style={memoStyles.recordingTime}>{formatTime(recordingTime)}</span>
            <span style={memoStyles.recordingLabel}>Recording…</span>
            <button type="button" style={memoStyles.stopBtn} onClick={stopRecording}>Stop</button>
          </div>
        )}
        {recordedUrl && (
          <div style={memoStyles.playbackRow}>
            <audio controls src={recordedUrl} style={memoStyles.audioPlayer} />
            <button type="button" style={memoStyles.rerecordBtn} onClick={reRecord}>Re-record</button>
          </div>
        )}
        {micError && <p style={memoStyles.errorText}>{micError}</p>}
      </div>

      {/* Upload fallback */}
      <div>
        <button type="button" style={memoStyles.uploadToggle} onClick={() => setShowUpload(v => !v)}>
          {showUpload ? 'Hide file upload' : 'Or upload a file instead (mp3, m4a, mp4, wav)'}
        </button>
        {showUpload && (
          <div style={memoStyles.fileRow}>
            <label style={memoStyles.fileLabel}>
              Choose audio file · 10 min max
              <input type="file" accept="audio/*,video/mp4" onChange={handleFileChange} style={{ display: 'none' }} />
            </label>
            {file && !recordedUrl && <span style={memoStyles.fileName}>{file.name}</span>}
          </div>
        )}
      </div>

      {durationError && <p style={memoStyles.errorText}>{durationError}</p>}
      {error && <p style={memoStyles.errorText}>{error}</p>}
      <button style={memoStyles.btn} type="submit" disabled={loading || !file}>
        {loading ? 'Uploading...' : 'Submit Your Story'}
      </button>
    </form>
  );
}

const memoStyles = {
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  input: { padding: '12px 16px', border: `1px solid #d4a096`, backgroundColor: '#fff', fontSize: '14px', fontFamily: 'Georgia, serif', outline: 'none', color: '#1a2e4a' },
  fileRow: { display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' },
  fileLabel: { display: 'inline-block', padding: '10px 20px', border: `1px dashed #d4a096`, fontSize: '13px', fontFamily: 'Georgia, serif', color: '#8b7a7a', cursor: 'pointer' },
  fileName: { fontSize: '12px', fontFamily: 'Georgia, serif', color: '#8b7a7a', fontStyle: 'italic' },
  btn: { alignSelf: 'flex-start', padding: '12px 28px', backgroundColor: '#c0736a', color: '#fff', border: 'none', fontSize: '14px', fontFamily: 'Georgia, serif', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px' },
  errorText: { color: '#c0392b', fontSize: '13px', fontFamily: 'Georgia, serif' },
  thanks: { padding: '24px', backgroundColor: '#fff', border: '1px solid #d4a096', borderLeft: '4px solid #c0736a' },
  thanksText: { fontFamily: 'Georgia, serif', color: '#1a2e4a', fontSize: '15px', fontStyle: 'italic', lineHeight: '1.8', margin: 0 },
  recorderBox: { display: 'flex', flexDirection: 'column', gap: '10px' },
  recordBtn: { alignSelf: 'flex-start', padding: '12px 28px', backgroundColor: '#1a2e4a', color: '#fff', border: 'none', fontSize: '14px', fontFamily: 'Georgia, serif', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px' },
  recordingRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', backgroundColor: '#fff', border: '1px solid #d4a096' },
  recordingDot: { width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#c0392b', flexShrink: 0, animation: 'pulse 1s infinite' },
  recordingTime: { fontFamily: 'Georgia, serif', fontSize: '15px', color: '#1a2e4a', fontWeight: '600', minWidth: '40px' },
  recordingLabel: { fontFamily: 'Georgia, serif', fontSize: '13px', color: '#8b7a7a', fontStyle: 'italic', flex: 1 },
  stopBtn: { padding: '8px 20px', backgroundColor: '#c0392b', color: '#fff', border: 'none', fontSize: '13px', fontFamily: 'Georgia, serif', fontWeight: '600', cursor: 'pointer' },
  playbackRow: { display: 'flex', flexDirection: 'column', gap: '8px' },
  audioPlayer: { width: '100%' },
  rerecordBtn: { alignSelf: 'flex-start', padding: '8px 18px', backgroundColor: 'transparent', border: '1px solid #d4a096', color: '#8b7a7a', fontSize: '13px', fontFamily: 'Georgia, serif', cursor: 'pointer' },
  uploadToggle: { background: 'none', border: 'none', color: '#8b7a7a', fontSize: '12px', fontFamily: 'Georgia, serif', fontStyle: 'italic', cursor: 'pointer', padding: 0, textDecoration: 'underline' },
};

export default function Listen() {
  const { user, token } = useAuth();
  const isSubscribed = user && user.subscription_status === 'active';

  const free = stories.filter(s => s.section === 'free');
  const members = stories.filter(s => s.section === 'member');
  const founder = stories.filter(s => s.section === 'founder');

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Listen — Real Divorce Stories | Elephants Answering</title>
        <meta name="description" content="Hear real stories from women who've been through divorce. Voice memos and audio stories shared by our community — for women starting out, in the middle of it, or rebuilding." />
      </Helmet>
      <SiteNav />

      <main style={styles.main}>
        <div style={styles.inner}>
          <p style={styles.eyebrow}>REAL STORIES</p>
          <h1 style={styles.title}>Listen</h1>
          <p style={styles.subtitle}>
            Women sharing their experiences — in their own words, at their own pace.
          </p>

          <section style={styles.section}>

            <div style={styles.grid}>
              {free.map(s => (
                <StoryCard key={s.id} story={s} isSubscribed={isSubscribed} token={token} user={user} />
              ))}
            </div>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionHeading}>Member Stories</h2>
            {!isSubscribed && (
              <p style={styles.memberNote}>Join the community to unlock all stories.</p>
            )}
            <div style={styles.grid}>
              {members.map(s => (
                <StoryCard key={s.id} story={s} isSubscribed={isSubscribed} token={token} user={user} />
              ))}
            </div>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionHeading}>Founder Stories</h2>
            {!isSubscribed && (
              <p style={styles.memberNote}>Join the community to unlock all stories.</p>
            )}
            <div style={styles.grid}>
              {founder.map(s => (
                <StoryCard key={s.id} story={s} isSubscribed={isSubscribed} token={token} user={user} />
              ))}
            </div>
          </section>

          {/* Submit a voice memo */}
          <section style={styles.section}>
            <h2 style={styles.sectionHeading}>Share Your Story</h2>
            <p style={styles.memberNote}>Have a story that could help another woman going through it? Will you share something you wished you'd known? Just click record or a voice memo — raw, unedited, 10 minutes or less.</p>
            <VoiceMemoForm />
          </section>

          {!isSubscribed && (
            <div style={styles.cta}>
              <p style={styles.ctaText}>Unlock every story when you join.</p>
              <Link to="/register" style={{ ...styles.ctaBtn, textDecoration: 'none', display: 'inline-block' }}>
                Join the Community
              </Link>
              <p style={styles.ctaTagline}>Free for Early Members</p>
            </div>
          )}
        </div>
      </main>
      <Disclaimer />
      <SiteFooter />
    </div>
  );
}

const styles = {
  page: { fontFamily: fonts.body, backgroundColor: colors.cream, minHeight: '100vh' },

  header: { backgroundColor: colors.white, padding: '16px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: `0 1px 0 ${colors.peach}`, position: 'sticky', top: 0, zIndex: 100 },
  logoArea: { display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' },
  logoIcon: { height: '88px', width: 'auto' },
  logoText: { fontFamily: fonts.heading, color: colors.navy, fontSize: '17px', fontWeight: '600', letterSpacing: '0.5px' },
  logoSub: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '11px', letterSpacing: '0.3px', marginTop: '3px' },
  headerLinks: { display: 'flex', gap: '12px', alignItems: 'center' },
  navBtn: { backgroundColor: 'transparent', border: 'none', color: colors.navy, padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontFamily: fonts.body, letterSpacing: '0.3px' },
  loginBtn: { backgroundColor: 'transparent', border: `1px solid ${colors.peach}`, color: colors.navy, padding: '8px 20px', borderRadius: '2px', cursor: 'pointer', fontSize: '13px', fontFamily: fonts.body },
  signupBtn: { backgroundColor: colors.rose, border: 'none', color: colors.white, padding: '8px 20px', borderRadius: '2px', cursor: 'pointer', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600' },

  main: { padding: '64px 20px 80px' },
  inner: { maxWidth: '900px', margin: '0 auto' },

  eyebrow: { fontFamily: fonts.body, color: colors.rose, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '12px' },
  title: { fontFamily: fonts.heading, color: colors.navy, fontSize: '36px', fontWeight: '600', marginBottom: '16px', letterSpacing: '0.3px' },
  subtitle: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '15px', lineHeight: '1.9', marginBottom: '56px' },

  section: { marginBottom: '60px' },
  sectionHeading: { fontFamily: fonts.heading, color: colors.navy, fontSize: '20px', fontWeight: '600', letterSpacing: '0.3px', marginBottom: '24px', paddingBottom: '12px', borderBottom: `2px solid ${colors.rose}` },
  memberNote: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', fontStyle: 'italic', marginBottom: '20px' },

  grid: { display: 'flex', flexDirection: 'column', gap: '20px' },

  card: { backgroundColor: colors.white, border: `1px solid ${colors.peach}`, borderTop: `3px solid ${colors.rose}`, padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: '8px' },
  cardTop: { display: 'flex', flexDirection: 'column', gap: '6px' },
  cardMeta: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' },
  freeBadge: { fontFamily: fonts.body, fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: colors.white, backgroundColor: colors.rose, padding: '3px 8px' },
  memberBadge: { fontFamily: fonts.body, fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: colors.navy, backgroundColor: colors.peach, padding: '3px 8px' },
  founderBadge: { fontFamily: fonts.body, fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: colors.white, backgroundColor: colors.navy, padding: '3px 8px' },
  duration: { fontFamily: fonts.body, fontSize: '12px', color: colors.textMedium },
  cardTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '17px', fontWeight: '600', letterSpacing: '0.3px', margin: 0 },
  cardSpeaker: { fontFamily: fonts.body, color: colors.rose, fontSize: '12px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', margin: 0 },
  cardDesc: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', lineHeight: '1.8', margin: 0 },

  cardBottom: {},
  comingSoonText: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', fontStyle: 'italic' },
  playBtn: { backgroundColor: colors.navy, color: colors.white, border: 'none', padding: '10px 28px', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px' },
  errorText: { fontFamily: fonts.body, color: colors.rose, fontSize: '12px', fontStyle: 'italic', marginTop: '8px' },

  locked: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  lockIcon: { fontSize: '16px' },
  lockedText: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', fontStyle: 'italic', margin: 0 },
  joinBtn: { backgroundColor: colors.rose, color: colors.white, border: 'none', padding: '10px 24px', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px' },

  cta: { marginTop: '16px', padding: '48px 40px', backgroundColor: colors.navy, textAlign: 'center' },
  ctaText: { fontFamily: fonts.heading, color: colors.white, fontSize: '18px', marginBottom: '20px', fontWeight: '500' },
  ctaBtn: { backgroundColor: colors.rose, color: colors.white, border: 'none', padding: '14px 36px', fontSize: '14px', cursor: 'pointer', fontWeight: '600', fontFamily: fonts.body, letterSpacing: '0.5px' },
  ctaTagline: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px', marginTop: '12px' },
  joinTagline: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '8px', textAlign: 'center' },
};
